import { ONE_HOUR_IN_MS } from "@/lib/constants";
import { StationData, StationResponse } from "@/lib/types/api/stations-data";
import { devStationId, isDevStation } from "@/lib/station-services/dev-stations";
import Logger from '@/lib/logger';
import { passCacheClientSingleton } from '@/lib/cache/client'
import cachedFetch from "@/lib/helpers/cached-fetch";
import { cacheKey } from "@/lib/helpers/cache-key";
import { StationDataCacheEmptyError, DuplicateSVPHostError } from "@/lib/errors";
import * as Sentry from "@sentry/nextjs";

const logger = new Logger('pbs:station-services:client')

const DISABLE_SELF_POPULATING_CACHE = process.env.DISABLE_SELF_POPULATING_CACHE === 'true' || false

const STATION_SERVICES_API_URL = process.env.STATION_SERVICES_API_URL
const STATION_SERVICES_PASSWORD = process.env.STATION_SERVICES_PASSWORD
const STATION_SERVICES_USERNAME = process.env.STATION_SERVICES_USERNAME

const stationUrl = `${STATION_SERVICES_API_URL}/api/v1/stations/`;

const STATIONS_CACHE_KEY = 'direct_station_data'
const STATION_HOST_CACHE_KEY = 'svp:host'
const STATION_CALLSIGN_CACHE_KEY = 'svp:callsign'
const STATION_HOST_TO_ID = 'svp:host_to_id'
const STATION_ID_VALID_CACHE_KEY = 'station_id:valid'
const STATION_CALLSIGN_VALID_CACHE_KEY = 'callsign:valid'


const hostKey = (host: string) => cacheKey(`${STATION_HOST_CACHE_KEY}:${host}`)
const callsignKey = (callsign: string) => cacheKey(`${STATION_CALLSIGN_CACHE_KEY}:${callsign}`)
const hostToIdKey = (host: string) => cacheKey(`${STATION_HOST_TO_ID}:${host}`)
const validIdKey = (id: string) => cacheKey(`${STATION_ID_VALID_CACHE_KEY}:${id}`)
const validCallsignKey = (callsign: string) => cacheKey(`${STATION_CALLSIGN_VALID_CACHE_KEY}:${callsign}`)

class Client {
  endpoint: string;
  authHeaders: Headers;

  constructor () {
    this.endpoint = stationUrl

    const headers = new Headers();
    const authHeaderString = `Basic ${Buffer.from(STATION_SERVICES_USERNAME + ':' + STATION_SERVICES_PASSWORD).toString('base64')}`;
    headers.append('Authorization', authHeaderString);
    this.authHeaders = headers
  }

  async cacheClient() {
    return await passCacheClientSingleton()
  }

  // returns JSON list of all stations from station services
  async getStations() {
    const cache = await this.cacheClient()
    logger.debug(`Checking cached stations under key ${STATIONS_CACHE_KEY}`)
    const key = cacheKey(STATIONS_CACHE_KEY)
    const cachedStations = await cache.get<StationData[]>(key)

    if(cachedStations) {
      return cachedStations
    }

    if(DISABLE_SELF_POPULATING_CACHE) {
      logger.error('Cached Station Data Expired and DISABLE_SELF_POPULATING_CACHE is set to', DISABLE_SELF_POPULATING_CACHE)
      logger.error('Check if background jobs are running to populate cache.')
      throw new StationDataCacheEmptyError('Station Data Cache Empty')
    }

    logger.debug('Cached Station Data Expired. Refetching...')
    let stations: StationData[] = [];
    let nextPage: string | null = stationUrl;
    while (nextPage !== null) {
      const response = await fetch(nextPage, { method: 'GET', headers: this.authHeaders });
      const json = await response.json() as StationResponse;
      stations = stations.concat(json.data);
      nextPage = json.links.next;
    }

    await this.populatePartitionedFullStationsCache(stations)
    logger.debug(`Station Data Cache Refilled with ${stations.length} stations`)
    return stations
  }

  async populatePartitionedFullStationsCache(stations: StationData[]) {
    const cache = await this.cacheClient()
    await cache.set(cacheKey(STATIONS_CACHE_KEY), stations, ONE_HOUR_IN_MS, { allowGraduatedResponse: true })

    this.reportDuplicateSVPHosts(stations)

    stations.forEach((station) => {
      logger.debug(`Iterating over ${station.attributes.call_sign}`)
      const host = this.getPortalHostnameForStation(station)
      const callsign = station.attributes?.call_sign.toLowerCase()

      const idKey = validIdKey(station.id)
      // The content here isn't valuable, the middleware will simply check if this
      // key exists to check validity.
      cache.set(idKey, true, ONE_HOUR_IN_MS, { allowGraduatedResponse: false })

      if(host) {
        const key = hostKey(host)
        cache.set(key, station, ONE_HOUR_IN_MS)
        logger.debug(`Set Station Data for Hostname under ${key}`)

        // Store the validity of the hostname and return the id for fast SVP
        // middleware checks
        const hostIdKey = hostToIdKey(host)
        cache.set(hostIdKey, station.id, ONE_HOUR_IN_MS)
        logger.debug(`Set Valid Host to ID under ${hostIdKey}`)
      }

      if(callsign) {
        const key = callsignKey(callsign)
        cache.set(key, station, ONE_HOUR_IN_MS, { allowGraduatedResponse: true })
        logger.debug(`Set Station Data for Callsign under ${key}`)

        // Store the validity of the callsign for middleware checks. Again the
        // content here isn't valuable, the middleware will simply check if this
        // key exists to confirm validity.
        const validCallKey = validCallsignKey(callsign)
        cache.set(validCallKey, true, ONE_HOUR_IN_MS)
        logger.debug(`Set Valid Callsign to Callsign under ${validCallKey}`)
      }
    })
  }

  getPortalHostnameForStation(station: StationData) {
    const videoPortal = station.attributes?.video_portal_url

    if (videoPortal) {
      return new URL(videoPortal).host;
    } else {
      return null;
    }
  }

  // RWEB-9915 Station Manager should have at most one station per SVP hostname, but
  // nothing enforces it — two stations sharing a video_portal_url leads to
  // ambiguous host -> station lookups (last-write-wins in the cache, first
  // match in any .find fallback). We don't throw, since a duplicate shouldn't
  // take the site down, but we surface it to Sentry keyed off call signs so
  // support can find and fix the offending stations in Station Manager.
  reportDuplicateSVPHosts(stations: StationData[]) {
    const callsignsByHost = new Map<string, string[]>()

    stations.forEach((station) => {
      const host = this.getPortalHostnameForStation(station)
      if (!host) {
        return
      }

      const callsigns = callsignsByHost.get(host) ?? []
      callsigns.push(station.attributes?.call_sign)
      callsignsByHost.set(host, callsigns)
    })

    callsignsByHost.forEach((callsigns, host) => {
      if (callsigns.length > 1) {
        this.captureDuplicateSVPHost(host, callsigns)
      }
    })
  }

  // Logs and reports a duplicate SVP host to Sentry, keyed off the conflicting
  // call signs so support can find the offending stations in Station Manager.
  private captureDuplicateSVPHost(host: string, callsigns: string[]) {
    const message = `Multiple stations share the SVP host "${host}": ${callsigns.join(', ')}. Clear the duplicate video_portal_url in Station Manager.`
    logger.error(message)
    Sentry.captureException(new DuplicateSVPHostError(message), {
      tags: { svpHost: host },
      extra: { host, callsigns },
    })
  }

  async possibleSVPHostToID(hostname: string): Promise<string | null> {
    if(isDevStation(hostname)) {
      return devStationId(hostname)
    }

    const cache = await this.cacheClient()
    const key = hostToIdKey(hostname)
    return await cache.get<string>(key)
  }

  // get full data object for an individual station by hostname
  async getStationDataByHostName(hostname: string): Promise<StationData | undefined> {
    const cache = await this.cacheClient()
    const key = hostKey(hostname)
    const cachedStationData = await cache.get<StationData>(key)
    if(cachedStationData) {
      logger.debug(`Hit on Cached Station Data by Hostname ${key}`)
      return cachedStationData
    }

    logger.debug(`Miss on Cached Station Data by Hostname ${key}`)

    const stations = await this.getStations();

    if (isDevStation(hostname)) {
      // we have station IDs hardcoded for dev stations
      const stationId = devStationId(hostname);
      const stationData = stations.find((station) =>
        station.id === stationId
      );
      return stationData
    } else {
      const matches = stations.filter((station) =>
        this.getPortalHostnameForStation(station) === hostname
      );

      if (matches.length > 1) {
        const callsigns = matches.map((station) => station.attributes?.call_sign)
        this.captureDuplicateSVPHost(hostname, callsigns)
      }

      return matches[0];
    }
  }

  async getStationDataByCallsign(call_sign: string): Promise<StationData | undefined> {
    const cache = await this.cacheClient()
    const key = callsignKey(call_sign)
    const cachedStationData = await cache.get<StationData>(key)
    if(cachedStationData) {
      logger.debug(`Hit on Cached Station Data by Callsign ${key}`)
      return cachedStationData
    }

    logger.debug(`Miss on Cached Station Data by Callsign ${key}`)

    const stations = await this.getStations();
    return stations.find((station) =>
      station.attributes?.call_sign.toLowerCase() === call_sign.toLowerCase()
    );
  }

  // get full data object for an individual station by id
  async getStationDataByStationId(stationId: string): Promise<StationData | undefined> {
    const url = `${stationUrl}${stationId}`;
    const json = await cachedFetch(url, ONE_HOUR_IN_MS, { method: 'GET', headers: this.authHeaders });
    return json.data;
  }

  // get station ID for an individual station by hostname
  async getStationIdByHostname(hostname: string): Promise<string | undefined> {
    if (isDevStation(hostname)) {
      return devStationId(hostname);
    }

    const station = await this.getStationDataByHostName(hostname);

    return station?.id;
  }

  async isValidStationId(stationId: string) {
    const cache = await this.cacheClient()
    const key = validIdKey(stationId)
    return await cache.exists(key)
  }

  async isValidStationCallsign(callsign: string) {
    const cache = await this.cacheClient()
    const key = validCallsignKey(callsign)
    return await cache.exists(key)
  }
}

const stationServices = new Client()

export default stationServices;
